import React, { useEffect, useMemo, useState } from 'react';

import { Tooltip } from '@/components/Tooltip';
import { getQueryEntity } from '@/sections/insights/queryBuilder/entities';
import type { SimpleFilterDefinition } from '@/sections/insights/queryBuilder/entities/types';

export interface QueryOrderByProps {
  value: string | undefined;
  editable: boolean;
  rootLabel?: string;
  onChange: (value: string | undefined) => void;
}

export function QueryOrderBy({
  value,
  editable,
  rootLabel,
  onChange,
}: QueryOrderByProps) {
  const tooltipContent = 'Sorts all table results by the specified field';
  const [simpleFilters, setSimpleFilters] = useState<
    SimpleFilterDefinition[] | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!rootLabel) {
      setSimpleFilters(null);
      return;
    }

    const entity = getQueryEntity(rootLabel);
    const getSimpleFilters = entity?.getSimpleFilters;
    if (!getSimpleFilters) {
      setSimpleFilters(null);
      return;
    }

    const loadSimpleFilters = async () => {
      setIsLoading(true);
      try {
        const definitions = await getSimpleFilters();
        setSimpleFilters(definitions);
      } catch (error) {
        console.error('Failed to load simple filters for order by:', error);
        setSimpleFilters(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadSimpleFilters();
  }, [rootLabel]);

  const options = useMemo(() => {
    if (!simpleFilters || simpleFilters.length === 0) return [];
    return simpleFilters.map(filter => ({
      label: filter.uiField,
      value: filter.apiField,
    }));
  }, [simpleFilters]);

  const displayValue = useMemo(() => {
    if (!value) return '(none)';
    const match = simpleFilters?.find(filter => filter.apiField === value);
    return match?.uiField || value;
  }, [simpleFilters, value]);

  if (editable) {
    if (options.length === 0) {
      return (
        <div className="flex items-center gap-2">
          <Tooltip title={tooltipContent}>
            <label
              htmlFor="query-orderby"
              className="text-chariot-text-secondary text-sm"
            >
              Order By:
            </label>
          </Tooltip>
          <input
            id="query-orderby"
            type="text"
            value={value ?? ''}
            onChange={e => onChange(e.target.value || undefined)}
            placeholder="Field name"
            className="bg-chariot-background-secondary text-chariot-text-primary border-chariot-stroke focus:border-chariot-brand-primary w-32 rounded-md border px-2 py-1 text-sm outline-none"
            data-testid="query-header-orderby-input"
          />
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <Tooltip title={tooltipContent}>
          <label
            htmlFor="query-orderby"
            className="text-chariot-text-secondary text-sm"
          >
            Order By:
          </label>
        </Tooltip>
        <select
          id="query-orderby"
          value={value ?? ''}
          onChange={e => onChange(e.target.value || undefined)}
          disabled={isLoading}
          className="bg-chariot-background-secondary text-chariot-text-primary border-chariot-stroke focus:border-chariot-brand-primary w-40 rounded-md border px-2 py-1 text-sm outline-none disabled:opacity-60"
          data-testid="query-header-orderby-input"
        >
          <option value="">
            {isLoading ? 'Loading fields...' : 'Select field'}
          </option>
          {options.map((option, idx) => (
            <option key={idx} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Tooltip title={tooltipContent}>
        <span className="text-chariot-text-secondary text-sm">Order By:</span>
      </Tooltip>
      <span className="text-chariot-text-primary text-sm">{displayValue}</span>
    </div>
  );
}
